import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Plus, 
  Search, 
  ArrowUpRight, 
  ArrowDownRight,
  ArrowRightLeft,
  ListChecks,
  ChevronLeft,
  ChevronRight,
  Check,
  CheckCircle2,
  Clock3,
  Trash2,
  SlidersHorizontal
} from "lucide-react";
import { useFinanceStore } from "../stores/finance-store";
import { cn, formatAmount, formatCurrency, formatDate, parseCurrency } from "../lib/utils";
import { CurrencyInput } from "../components/CurrencyInput";
import { DataTable } from "../components/DataTable";
import { Switch } from "../components/ui/switch";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { AddTransactionDialog } from "../components/AddTransactionDialog";
import { MonthSaldoBar } from "../components/MonthSaldoBar";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  buildAccountStatementRows,
  filterByStatus,
  filterVisibleTransactions,
  getAccountStatementBalanceValue,
  getEffectiveTransactionDate,
  getSignedTransactionAmount,
  getTransferGroupRows,
  isTransactionInMonth,
} from "../lib/finance-calculations";
import {
  findAccountByIdOrName,
  getAccountDisplayName,
  transactionMatchesAccount,
} from "../lib/account-relations";
import { formatTransactionCategoryName, getTransactionCategoryName } from "../lib/category-relations";
import {
  getTransactionKind,
  getTransactionKindLabel,
  isDetailedTransaction,
  isBalanceAdjustmentTransaction,
  isExpenseForReports,
  isIncomeForReports,
  isTransferTransaction,
  TRANSACTION_KINDS,
} from "../lib/transaction-types";
import { prepareTransactionClone } from "../lib/transaction-clone";

function parseMonthParam(value) {
  const month = Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? month - 1 : null;
}

function parseYearParam(value) {
  const year = Number(value);
  return Number.isInteger(year) && year >= 1900 && year <= 9999 ? year : null;
}

function isSameMonth(dateValue, month, year) {
  const date = new Date(dateValue || 0);
  return date.getMonth() === month && date.getFullYear() === year;
}

function parseDateInputValue(value, endOfDay = false) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
}

function isDateInRange(dateValue, startValue, endValue) {
  const date = new Date(dateValue || 0);
  if (Number.isNaN(date.getTime())) return false;

  let start = parseDateInputValue(startValue);
  let end = parseDateInputValue(endValue, true);

  if (start && end && start > end) {
    [start, end] = [parseDateInputValue(endValue), parseDateInputValue(startValue, true)];
  }

  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

function areStringArraysEqual(first = [], second = []) {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
}

export default function TransactionsPage({ accountStatementMode = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get("month");
  const yearParam = searchParams.get("year");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [transactionInitialData, setTransactionInitialData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypeFilters, setSelectedTypeFilters] = useState([]);
  const [selectedAccountFilters, setSelectedAccountFilters] = useState([]);
  const [dateStartFilter, setDateStartFilter] = useState("");
  const [dateEndFilter, setDateEndFilter] = useState("");
  const [draftTypeFilters, setDraftTypeFilters] = useState([]);
  const [draftAccountFilters, setDraftAccountFilters] = useState([]);
  const [draftDateStartFilter, setDraftDateStartFilter] = useState("");
  const [draftDateEndFilter, setDraftDateEndFilter] = useState("");
  const [realizadoSaldo, setRealizadoSaldo] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  const {
    transactions,
    accounts,
    categories,
    fetchTransactions,
    fetchAccounts,
    fetchCategories,
    deleteTransaction,
    addTransaction,
    updateTransaction,
    updateTransactionBatch,
    isLoading,
  } = useFinanceStore();
  const [openingBalanceDialog, setOpeningBalanceDialog] = useState({
    open: false,
    row: null,
    value: "",
  });
  const [balanceAdjustmentToDelete, setBalanceAdjustmentToDelete] = useState(null);
  const [isDeletingBalanceAdjustment, setIsDeletingBalanceAdjustment] = useState(false);
  
  useEffect(() => {
    fetchTransactions();
    fetchAccounts();
    fetchCategories();
  }, [fetchTransactions, fetchAccounts, fetchCategories]);
  
  const handleDeleteFromDialog = async (tx, scope = "current") => {
    if (!tx?.id) return { success: false };

    const result = await deleteTransaction(tx.id, scope);
    if (result.success) {
      toast.success("Transação excluída!");
      setShowAddDialog(false);
      setEditData(null);
    } else {
      toast.error("Erro ao excluir transação");
    }
    return result;
  };

  const openDeleteBalanceAdjustmentDialog = (row) => {
    const tx = row?.transaction || row;
    if (row?.rowType !== "balance_adjustment" || !isBalanceAdjustmentTransaction(tx)) return;
    setBalanceAdjustmentToDelete(row);
  };

  const closeDeleteBalanceAdjustmentDialog = () => {
    if (isDeletingBalanceAdjustment) return;
    setBalanceAdjustmentToDelete(null);
  };

  const handleDeleteBalanceAdjustment = async () => {
    const row = balanceAdjustmentToDelete;
    const tx = row?.transaction || row;

    if (row?.rowType !== "balance_adjustment" || !isBalanceAdjustmentTransaction(tx) || !tx?.id) {
      toast.error("Apenas ajustes de saldo podem ser excluídos por aqui");
      setBalanceAdjustmentToDelete(null);
      return;
    }

    setIsDeletingBalanceAdjustment(true);
    const result = await deleteTransaction(tx.id);
    setIsDeletingBalanceAdjustment(false);

    if (result.success) {
      toast.success("Ajuste de saldo excluído");
      setBalanceAdjustmentToDelete(null);
    } else {
      toast.error("Erro ao excluir ajuste de saldo");
    }
  };
  
  const handleToggleStatus = async (tx) => {
    const newStatus = !tx.status;
    const realizedAt = newStatus ? new Date().toISOString() : undefined;

    const withReconciliation = (item) => {
      const next = {
        ...item,
        status: newStatus,
        confirmed: newStatus,
      };

      if (newStatus) {
        next.realized_at = realizedAt;
      } else {
        delete next.realized_at;
      }

      return next;
    };

    if (isTransferTransaction(tx)) {
      const linkedTx = tx.transferPair
        ? null
        : tx.linked_transfer_id
        ? transactions.find((item) => item.linked_transfer_id === tx.linked_transfer_id && item.id !== tx.id)
        : null;
      const transferItems = tx.transferPair || [tx, linkedTx].filter(Boolean);
      const updates = transferItems.map((item) => ({
        id: item.id,
        type: item.type,
        data: withReconciliation(item),
      }));
      const result = await updateTransactionBatch(updates);
      if (result.success) {
        toast.success(newStatus ? "Transferência conciliada" : "Transferência pendente");
      } else {
        toast.error("Erro ao atualizar conciliação");
      }
      return;
    }

    const result = await updateTransaction(tx.id, tx.type, {
      ...withReconciliation(tx),
      ...(isDetailedTransaction(tx) ? { confirmed: newStatus } : {}),
    });
    if (result.success) {
      toast.success(newStatus ? "Conciliado" : "Pendente");
    } else {
      toast.error("Erro ao atualizar conciliação");
    }
  };
  
  const handleEdit = (tx) => {
    setTransactionInitialData(null);
    setEditData(tx);
    setShowAddDialog(true);
  };
  
  const [currentMonth, setCurrentMonth] = useState(() => parseMonthParam(monthParam) ?? new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(() => parseYearParam(yearParam) ?? new Date().getFullYear());

  useEffect(() => {
    const nextMonth = parseMonthParam(monthParam);
    const nextYear = parseYearParam(yearParam);

    if (nextMonth !== null) setCurrentMonth(nextMonth);
    if (nextYear !== null) setCurrentYear(nextYear);
  }, [monthParam, yearParam]);
  
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const transactionTypeOptions = [
    { value: TRANSACTION_KINDS.EXPENSE, label: "Despesas" },
    { value: TRANSACTION_KINDS.INCOME, label: "Receitas" },
    { value: TRANSACTION_KINDS.TRANSFER, label: "Transferências" },
    { value: TRANSACTION_KINDS.DETAILED, label: "Detalhadas" },
  ];

  const visibleTransactions = useMemo(() => filterVisibleTransactions(transactions), [transactions]);

  const statementAccounts = useMemo(() => {
    return accounts
      .filter((account) => account.id && account.name)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [accounts]);

  const accountOptions = useMemo(() => {
    return statementAccounts.map((account) => ({ value: account.id, label: account.name }));
  }, [statementAccounts]);

  const defaultStatementAccountId = useMemo(() => {
    const activeAccounts = statementAccounts
      .filter((account) => account.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

    return activeAccounts[0]?.id || statementAccounts[0]?.id || "";
  }, [statementAccounts]);

  const accountIdParam = searchParams.get("account_id");
  const legacyAccountParam = searchParams.get("account");
  const selectedAccountParam = accountIdParam || findAccountByIdOrName(accounts, null, legacyAccountParam)?.id || "";

  useEffect(() => {
    if (accountStatementMode) {
      const accountExists = accountOptions.some((option) => option.value === selectedAccountParam);
      if (selectedAccountParam && accountExists) {
        setSelectedAccountFilters([selectedAccountParam]);
      } else if (defaultStatementAccountId) {
        setSelectedAccountFilters([defaultStatementAccountId]);
      } else if (accountOptions.length === 0) {
        setSelectedAccountFilters([]);
      }
      return;
    }

    if (!selectedAccountParam) {
      setSelectedAccountFilters([]);
      return;
    }

    const accountExists = accountOptions.some((option) => option.value === selectedAccountParam);
    if (accountExists) {
      setSelectedAccountFilters([selectedAccountParam]);
    } else if (accountOptions.length > 0) {
      setSelectedAccountFilters([]); 
    }
  }, [selectedAccountParam, accountOptions, accountStatementMode, defaultStatementAccountId]);

  useEffect(() => {
    setDraftTypeFilters(selectedTypeFilters);
  }, [selectedTypeFilters]);

  useEffect(() => {
    setDraftAccountFilters(selectedAccountFilters);
  }, [selectedAccountFilters]);

  useEffect(() => {
    setDraftDateStartFilter(dateStartFilter);
  }, [dateStartFilter]);

  useEffect(() => {
    setDraftDateEndFilter(dateEndFilter);
  }, [dateEndFilter]);

  const handleSelectStatementAccount = (accountId) => {
    if (!accountId) return;

    setSelectedAccountFilters([accountId]);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set("account_id", accountId);
      nextParams.delete("account");
      return nextParams;
    });
  };

  const getLinkedTransfer = (tx) => (
    tx.linked_transfer_id
      ? transactions.find((item) => item.linked_transfer_id === tx.linked_transfer_id && item.id !== tx.id)
      : null
  );

  const handleCloneTransaction = (tx) => {
    if (!tx) return;
    const clone = prepareTransactionClone(tx, {
      linkedTransfer: getLinkedTransfer(tx),
      now: new Date(),
    });
    setEditData(null);
    setTransactionInitialData(clone);
    setShowAddDialog(true);
  };

  const matchesAccountFilter = (tx) => {
    if (selectedAccountFilters.length === 0) return true;

    return selectedAccountFilters.some((accountId) => {
      const account = accounts.find((item) => item.id === accountId);
      return account ? transactionMatchesAccount(tx, account, accounts, getLinkedTransfer(tx)) : false;
    });
  };

  const hasDateRangeFilter = Boolean(dateStartFilter || dateEndFilter);

  const matchesDateFilter = (tx, realizedOnly) => {
    const dateValue = accountStatementMode
      ? tx.date
      : getEffectiveTransactionDate(tx, realizedOnly);

    if (hasDateRangeFilter) {
      return isDateInRange(dateValue, dateStartFilter, dateEndFilter);
    }

    return accountStatementMode
      ? isSameMonth(tx.date, currentMonth, currentYear)
      : isTransactionInMonth(tx, currentMonth, currentYear, realizedOnly);
  };

  const getFilteredTransactionsForMode = (realizedOnly) => visibleTransactions.filter((tx) => {
    if (!matchesDateFilter(tx, realizedOnly)) return false;
    if (realizedOnly && tx.status !== true) return false;

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const categoryName = getTransactionCategoryName(tx, categories).toLowerCase();
    const matchesSearch = !normalizedSearch ||
      tx.description?.toLowerCase().includes(normalizedSearch) ||
      categoryName.includes(normalizedSearch);
    const matchesType = (() => {
      if (selectedTypeFilters.length === 0) return true;
      return selectedTypeFilters.some((typeFilter) => {
        if (typeFilter === TRANSACTION_KINDS.TRANSFER) return isTransferTransaction(tx);
        if (typeFilter === TRANSACTION_KINDS.DETAILED) return isDetailedTransaction(tx);
        if (typeFilter === TRANSACTION_KINDS.INCOME) return isIncomeForReports(tx);
        if (typeFilter === TRANSACTION_KINDS.EXPENSE) return isExpenseForReports(tx);
        return tx.type === typeFilter;
      });
    })();
    return matchesSearch && matchesType && matchesAccountFilter(tx);
  });

  const filteredTransactions = getFilteredTransactionsForMode(realizadoSaldo);
  
  // Sorted list of filtered transactions
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => (
      new Date(getEffectiveTransactionDate(a, realizadoSaldo) || 0) -
      new Date(getEffectiveTransactionDate(b, realizadoSaldo) || 0)
    ));
  }, [filteredTransactions, realizadoSaldo]);

  const statementAccount = useMemo(() => {
    if (!accountStatementMode) return null;
    if (selectedAccountFilters.length !== 1) return null;
    return accounts.find((account) => account.id === selectedAccountFilters[0]) || null;
  }, [accounts, selectedAccountFilters, accountStatementMode]);

  const filteredTransactionIds = useMemo(() => {
    return new Set(filteredTransactions.map((tx) => tx.id));
  }, [filteredTransactions]);

  const statementRows = useMemo(() => {
    if (!statementAccount) return [];

    return buildAccountStatementRows({
      account: statementAccount,
      transactions,
      month: currentMonth,
      year: currentYear,
      realizadoOnly: realizadoSaldo,
      accounts,
      transactionPredicate: (tx) => filteredTransactionIds.has(tx.id),
    });
  }, [statementAccount, transactions, currentMonth, currentYear, realizadoSaldo, filteredTransactionIds, accounts]);

  const statementMonthBalances = useMemo(() => {
    if (!statementAccount) return null;

    return {
      projected: getAccountStatementBalanceValue(statementAccount, transactions, false, {
        month: currentMonth,
        year: currentYear,
        accounts,
      }),
      realized: getAccountStatementBalanceValue(statementAccount, transactions, true, {
        month: currentMonth,
        year: currentYear,
        accounts,
      }),
    };
  }, [statementAccount, transactions, currentMonth, currentYear, accounts]);

  const createTransactionInitialData = useMemo(() => (
    accountStatementMode && statementAccount
      ? {
          account: statementAccount.name,
          account_id: statementAccount.id,
        }
      : null
  ), [accountStatementMode, statementAccount]);

  const groupedTransactions = useMemo(() => {
    return getTransferGroupRows(sortedTransactions, visibleTransactions, realizadoSaldo, accounts);
  }, [sortedTransactions, visibleTransactions, realizadoSaldo, accounts]);

  const displayRows = accountStatementMode ? statementRows : groupedTransactions;
  
  const getTotals = (source) => ({
    income: source.filter(isIncomeForReports)
                  .reduce((acc, tx) => acc + parseCurrency(tx.amount), 0),
    expense: source.filter(isExpenseForReports)
                   .reduce((acc, tx) => acc + parseCurrency(tx.amount), 0)
  });
  const projectedTotals = getTotals(getFilteredTransactionsForMode(false));
  const realizedTotals = getTotals(filterByStatus(getFilteredTransactionsForMode(true), true));

  const monthName = new Date(currentYear, currentMonth, 1).toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  const hasPendingFilterChanges = (
    draftDateStartFilter !== dateStartFilter ||
    draftDateEndFilter !== dateEndFilter ||
    !areStringArraysEqual(draftTypeFilters, selectedTypeFilters) ||
    !areStringArraysEqual(draftAccountFilters, selectedAccountFilters)
  );
  const hasActiveFilters = searchTerm || hasDateRangeFilter || selectedTypeFilters.length > 0 || selectedAccountFilters.length > 0;
  const hasManualFilters = hasDateRangeFilter || selectedTypeFilters.length > 0 || (!accountStatementMode && selectedAccountFilters.length > 0) || hasPendingFilterChanges;
  const handleApplyFilters = () => {
    setDateStartFilter(draftDateStartFilter);
    setDateEndFilter(draftDateEndFilter);
    setSelectedTypeFilters(draftTypeFilters);

    if (accountStatementMode) {
      const accountId = draftAccountFilters[0];
      if (accountId && accountId !== selectedAccountFilters[0]) {
        handleSelectStatementAccount(accountId);
      }
      return;
    }

    setSelectedAccountFilters(draftAccountFilters);
  };
  const openCreateDialog = () => {
    setEditData(null);
    setTransactionInitialData(null);
    setShowAddDialog(true);
  };
  const openOpeningBalanceDialog = (row) => {
    if (!statementAccount || row?.rowType !== "opening") return;

    setOpeningBalanceDialog({
      open: true,
      row,
      value: String(row.balanceAfter ?? 0),
    });
  };
  const closeOpeningBalanceDialog = () => {
    setOpeningBalanceDialog({ open: false, row: null, value: "" });
  };
  const handleOpeningBalanceSubmit = async (event) => {
    event.preventDefault();
    if (!statementAccount || !openingBalanceDialog.row) return;

    const targetBalance = parseCurrency(openingBalanceDialog.value);
    if (Number.isNaN(targetBalance)) {
      toast.error("Saldo inválido");
      return;
    }

    const currentBalance = Number(openingBalanceDialog.row.balanceAfter) || 0;
    const diff = targetBalance - currentBalance;

    if (Math.abs(diff) < 0.00001) {
      toast.info("Saldo inicial já está correto");
      closeOpeningBalanceDialog();
      return;
    }

    const adjustmentDate = new Date(currentYear, currentMonth, 1, 0, 0, 0, 0);
    adjustmentDate.setMilliseconds(-1);

    const result = await addTransaction(diff > 0 ? "income" : "expense", {
      description: "Ajuste de Saldo",
      amount: Math.abs(diff),
      category: "AJUSTE",
      account_id: statementAccount.id,
      account: statementAccount.name,
      date: adjustmentDate.toISOString(),
      realized_at: adjustmentDate.toISOString(),
      status: true,
      confirmed: true,
      transaction_kind: TRANSACTION_KINDS.BALANCE_ADJUSTMENT,
      hidden: true,
      affects_reports: false,
    });

    if (result.success) {
      toast.success("Saldo inicial ajustado");
      closeOpeningBalanceDialog();
    } else {
      toast.error("Erro ao ajustar saldo inicial");
    }
  };
  const emptyState = (
    <div className="empty-state py-12">
      <p className="text-muted-foreground mb-4">
        {hasActiveFilters
          ? "Nenhuma transação encontrada"
          : "Nenhuma transação registrada"}
      </p>
      {!hasActiveFilters && (
        <Button onClick={openCreateDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Transação
        </Button>
      )}
    </div>
  );
  const statementCurrency = statementAccount?.currency || "BRL";
  const pageTitle = accountStatementMode ? "Extrato de Conta" : "Transações";
  const pageDescription = accountStatementMode
    ? ""
    : `${visibleTransactions.length} transações registradas`;
  const statementColumns = [
    {
      key: "date",
      label: "Data",
      className: "hidden sm:table-cell whitespace-nowrap",
      render: (row) => formatDate(row.date),
    },
    {
      key: "description",
      label: "Descrição",
      className: "min-w-0 px-1 sm:px-2",
      render: (row) => {
        if (row.rowType === "opening" || row.rowType === "daily_balance") {
          return (
            <span className={`font-semibold text-muted-foreground ${row.rowType === "opening" ? "underline-offset-2 group-hover:underline" : ""}`}>
              {row.description}
            </span>
          );
        }

        if (row.rowType === "balance_adjustment") {
          return (
            <span className="font-semibold text-muted-foreground">
              Ajuste de saldo
            </span>
          );
        }

        const tx = row.transaction || row;
        const cleanDesc = tx.description ? tx.description.replace(/\s*\(\d+\/\d+\)$/, "") : "Sem descrição";
        const kind = getTransactionKind(tx);
        return (
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <div className={`shrink-0 rounded-full p-1 sm:p-1.5 ${kind === TRANSACTION_KINDS.TRANSFER ? "bg-primary/10" : tx.type === "income" ? "bg-success/10" : "bg-destructive/10"}`}>
              {kind === TRANSACTION_KINDS.TRANSFER ? (
                <ArrowRightLeft className="w-3.5 h-3.5 text-primary" />
              ) : kind === TRANSACTION_KINDS.DETAILED ? (
                <ListChecks className="w-3.5 h-3.5 text-destructive" />
              ) : tx.type === "income" ? (
                <ArrowUpRight className="w-3.5 h-3.5 text-success" />
              ) : (
                <ArrowDownRight className="w-3.5 h-3.5 text-destructive" />
              )}
            </div>
            <span className="max-w-[220px] truncate font-medium">{cleanDesc}</span>
          </div>
        );
      },
    },
    {
      key: "previousBalance",
      label: "Saldo anterior",
      align: "right",
      className: "hidden lg:table-cell",
      render: (row) => (row.rowType === "transaction" || row.rowType === "balance_adjustment")
        ? <span className={`font-mono text-sm ${row.affectsStatementBalance === false ? "text-muted-foreground" : ""}`}>{formatCurrency(row.previousBalance, statementCurrency)}</span>
        : "—",
    },
    {
      key: "amount",
      label: "Valor",
      align: "right",
      render: (row) => {
        if (row.rowType !== "transaction" && row.rowType !== "balance_adjustment") return "—";
        const amount = row.transactionAmount;
        const color = amount >= 0 ? "text-success" : "text-destructive";
        const isReconciledNegative = row.rowType === "transaction" && row.transaction?.status && amount < 0;
        return (
          <span className={`font-mono text-sm font-semibold ${color} ${row.affectsStatementBalance === false ? "opacity-60" : ""} ${isReconciledNegative ? "line-through opacity-70" : ""}`}>
            {amount >= 0 ? "+" : "-"}{formatAmount(amount)}
          </span>
        );
      },
    },
    {
      key: "balanceAfter",
      label: "Saldo depois",
      align: "right",
      render: (row) => {
        const color = row.affectsStatementBalance === false
          ? "text-muted-foreground"
          : row.balanceAfter < 0 ? "text-destructive" : "text-foreground";
        return (
          <span className={`font-mono text-sm font-semibold ${color}`}>
            {formatCurrency(row.balanceAfter, statementCurrency)}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Conciliado",
      align: "right",
      className: "w-[80px]",
      render: (row) => {
        if (row.rowType !== "transaction") return null;
        const tx = row.transaction || row;
        return (
          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={!!tx.status}
              onCheckedChange={() => handleToggleStatus(tx)}
              aria-label="Marcar como conciliado"
            />
          </div>
        );
      },
    },
    {
      key: "action",
      label: "",
      align: "right",
      className: "w-10 px-1",
      render: (row) => {
        if (row.rowType === "balance_adjustment") {
          return (
            <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 min-w-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => openDeleteBalanceAdjustmentDialog(row)}
                aria-label="Excluir ajuste de saldo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        }

        if (row.rowType !== "transaction") return null;
        const tx = row.transaction || row;
        return (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 min-w-8 px-2 text-lg font-semibold"
              onClick={() => handleEdit(tx)}
              aria-label="Visualizar detalhes da transação"
            >
              &gt;
            </Button>
          </div>
        );
      },
    },
  ];
  
  return (
    <div className="space-y-6 pb-32 sm:pb-28">
      {/* Header */}
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{pageTitle}</h1>
          {pageDescription && (
            <p className="text-muted-foreground mt-1">
              {pageDescription}
            </p>
          )}
        </div>
      </div>

      {accountStatementMode && statementAccounts.length === 0 && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            Nenhuma conta cadastrada para visualizar o extrato.
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2 lg:grid-cols-[auto_minmax(140px,220px)_auto_minmax(260px,1fr)_auto]">
              <Button variant="outline" size="sm" onClick={handlePrevMonth} className="px-2 sm:px-3">
                <ChevronLeft className="w-4 h-4 sm:hidden" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>
              <span className="min-w-0 text-center text-sm font-semibold capitalize sm:text-base">{monthName}</span>
              <Button variant="outline" size="sm" onClick={handleNextMonth} className="px-2 sm:px-3">
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight className="w-4 h-4 sm:hidden" />
              </Button>
              <div className="relative col-span-4 row-start-2 lg:col-span-1 lg:row-start-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar transações..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-transactions"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(
                  "h-9 w-9",
                  (filtersOpen || hasManualFilters) && "border-primary bg-primary/10 text-primary"
                )}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-label={filtersOpen ? "Recolher filtros" : "Mostrar filtros"}
                aria-expanded={filtersOpen}
                title={filtersOpen ? "Recolher filtros" : "Mostrar filtros"}
                data-testid="toggle-filters"
              >
                <SlidersHorizontal className="h-5 w-5" />
              </Button>
            </div>
            <div className={cn(
              "items-end gap-2 rounded-lg border bg-muted/20 p-3",
              "sm:grid-cols-2 xl:grid-cols-[150px_150px_minmax(180px,1fr)_minmax(180px,1fr)_auto]",
              filtersOpen ? "grid" : "hidden"
            )}>
              <div className="space-y-1.5">
                <Label htmlFor="transaction-date-start" className="text-xs font-semibold text-muted-foreground">
                  Dia início
                </Label>
                <Input
                  id="transaction-date-start"
                  type="date"
                  value={draftDateStartFilter}
                  onChange={(event) => setDraftDateStartFilter(event.target.value)}
                  className="h-9 px-2 text-sm"
                  data-testid="date-start-filter"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="transaction-date-end" className="text-xs font-semibold text-muted-foreground">
                  Dia fim
                </Label>
                <Input
                  id="transaction-date-end"
                  type="date"
                  value={draftDateEndFilter}
                  onChange={(event) => setDraftDateEndFilter(event.target.value)}
                  className="h-9 px-2 text-sm"
                  data-testid="date-end-filter"
                />
              </div>
              <MultiSelectDropdown
                label="Tipos"
                allLabel="Todas"
                options={transactionTypeOptions}
                selectedValues={draftTypeFilters}
                onChange={setDraftTypeFilters}
                className="h-9 w-full"
              />
              {accountStatementMode && (
                <div className="space-y-1.5">
                  <Label htmlFor="statement-account-filter" className="text-xs font-semibold text-muted-foreground">
                    Contas
                  </Label>
                  <Select
                    value={draftAccountFilters[0] || ""}
                    onValueChange={(accountId) => setDraftAccountFilters([accountId])}
                    disabled={accountOptions.length === 0}
                  >
                    <SelectTrigger id="statement-account-filter" className="h-9" data-testid="statement-account-filter">
                      <SelectValue placeholder="Selecione uma conta" />
                    </SelectTrigger>
                    <SelectContent>
                      {accountOptions.map((account) => (
                        <SelectItem key={account.value} value={account.value}>
                          {account.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!accountStatementMode && (
                <MultiSelectDropdown
                  label="Contas"
                  allLabel="Todas"
                  options={accountOptions}
                  selectedValues={draftAccountFilters}
                  onChange={setDraftAccountFilters}
                  className="h-9 w-full"
                />
              )}
              <Button
                type="button"
                className="h-9 w-full gap-2 px-4 xl:w-auto"
                onClick={handleApplyFilters}
                disabled={!hasPendingFilterChanges}
                data-testid="apply-transaction-filters"
              >
                <Check className="h-4 w-4" />
                Aplicar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Saldo Bar */}
      <MonthSaldoBar 
        projectedIncome={projectedTotals.income}
        projectedExpense={projectedTotals.expense}
        projectedBalance={accountStatementMode ? statementMonthBalances?.projected : undefined}
        realizedIncome={realizedTotals.income}
        realizedExpense={realizedTotals.expense}
        realizedBalance={accountStatementMode ? statementMonthBalances?.realized : undefined}
        realizadoOnly={realizadoSaldo} 
        onToggle={setRealizadoSaldo} 
      />
      
      {/* Transactions List */}
      <MobileTransactionsList
        transactions={displayRows}
        isLoading={isLoading}
        emptyState={emptyState}
        onEdit={handleEdit}
        onEditOpeningBalance={openOpeningBalanceDialog}
        onDeleteBalanceAdjustment={openDeleteBalanceAdjustmentDialog}
        onToggleStatus={handleToggleStatus}
        getLinkedTransfer={getLinkedTransfer}
        accounts={accounts}
        statementMode={accountStatementMode}
        statementCurrency={statementAccount?.currency || "BRL"}
        categories={categories}
      />

      <Card className="hidden sm:block">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={accountStatementMode ? statementColumns : [
                {
                  key: "date",
                  label: "Data",
                  className: "hidden sm:table-cell whitespace-nowrap",
                  render: (row) => formatDate(row.date)
                },
                { 
                  key: "description", 
                  label: "Descrição", 
                  className: "min-w-0 px-1 sm:px-2",
                  render: (row) => {
                    const cleanDesc = row.description ? row.description.replace(/\s*\(\d+\/\d+\)$/, "") : "Sem descrição";
                    const kind = getTransactionKind(row);
                    const kindBadge = (kind === TRANSACTION_KINDS.TRANSFER || kind === TRANSACTION_KINDS.DETAILED) ? (
                      <span className="ml-0 mt-1 inline-flex w-fit items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary sm:ml-2 sm:mt-0">
                        {getTransactionKindLabel(kind)}
                      </span>
                    ) : null;
                    let badge = null;
                    if (row.recurrence_type === "fixed") {
                      badge = <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 w-fit text-[10px] uppercase font-bold tracking-wider bg-primary/10 text-primary px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0" title="Fixa"><span className="text-[10px]">📌</span> FIXA</span>;
                    } else if (row.recurrence_type === "installment" || row.recurrence_id) {
                      badge = <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 w-fit text-[10px] uppercase font-bold tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0" title="Repetição"><span className="text-[10px]">🔁</span> PARCELADO</span>;
                    }
                    const iconClass = kind === TRANSACTION_KINDS.TRANSFER
                      ? "bg-primary/10"
                      : row.type === "income" ? "bg-success/10" : "bg-destructive/10";
                    return (
                      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                        <div className={`shrink-0 rounded-full p-1 sm:p-1.5 ${iconClass}`}>
                          {kind === TRANSACTION_KINDS.TRANSFER ? (
                            <ArrowRightLeft className="w-3.5 h-3.5 text-primary" />
                          ) : kind === TRANSACTION_KINDS.DETAILED ? (
                            <ListChecks className="w-3.5 h-3.5 text-destructive" />
                          ) : row.type === "income" ? (
                            <ArrowUpRight className="w-3.5 h-3.5 text-success" />
                          ) : (
                            <ArrowDownRight className="w-3.5 h-3.5 text-destructive" />
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="max-w-[92px] truncate font-medium sm:max-w-[200px]">{cleanDesc}</span>
                          {kindBadge}
                          {badge}
                        </div>
                      </div>
                    );
                  }
                },
                { 
                  key: "category", 
                  label: "Categoria", 
                  className: "hidden md:table-cell",
                  render: (row) => formatTransactionCategoryName(row, categories)
                },
                { 
                  key: "account", 
                  label: "Conta", 
                  className: "hidden md:table-cell",
                  render: (row) => {
                    return getAccountDisplayName(row, accounts, isTransferTransaction(row) ? getLinkedTransfer(row) : null);
                  } 
                },
                { 
                  key: "amount", 
                  label: "Valor", 
                  align: "right",
                  className: "w-[78px] px-1 sm:w-auto sm:px-2",
                  render: (row) => {
                    const neutralTransfer = isTransferTransaction(row);
                    const signedAmount = neutralTransfer ? parseCurrency(row.amount) : getSignedTransactionAmount(row);
                    const amountColor = neutralTransfer
                      ? "text-primary"
                      : signedAmount >= 0
                        ? "text-success"
                        : "text-destructive";
                    return (
                      <span className={`font-mono text-xs font-semibold sm:text-sm ${amountColor} ${(row.status && signedAmount < 0 && !neutralTransfer) ? "line-through opacity-70" : ""} ${(row.status && signedAmount >= 0 && !neutralTransfer) ? "opacity-70" : ""}`}>
                        {neutralTransfer ? "" : signedAmount >= 0 ? "+" : "-"}{formatAmount(signedAmount)}
                      </span>
                    );
                  }
                },
                {
                  key: "qtd",
                  label: "Qtd",
                  align: "center",
                  className: "hidden md:table-cell",
                  render: (row) => {
                    const qtyMatch = row.description && row.description.match(/\(((\d+)\/(\d+))\)$/);
                    const displayCount = qtyMatch ? qtyMatch[1] : 1;
                    return (
                      <span className="inline-flex items-center justify-center bg-muted text-muted-foreground text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                        {displayCount}
                      </span>
                    );
                  }
                },
                {
                  key: "status",
                  label: (
                    <>
                      <span className="hidden sm:inline">Conciliado</span>
                      <span className="sm:hidden">Conc.</span>
                    </>
                  ),
                  align: "right",
                  className: "w-[48px] px-1 sm:w-auto sm:px-2",
                  render: (row) => (
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex w-8 scale-90 items-center justify-center sm:w-10 sm:scale-100">
                        <Switch 
                          checked={!!row.status} 
                          onCheckedChange={() => handleToggleStatus(row)} 
                          aria-label="Marcar como conciliado"
                        />
                      </div>
                    </div>
                  )
                },
                {
                  key: "action",
                  label: "",
                  align: "right",
                  className: "w-7 px-0 sm:w-10 sm:px-1",
                  render: (row) => (
                    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 min-w-6 px-1 text-base font-semibold sm:h-8 sm:min-w-8 sm:px-2 sm:text-lg"
                        onClick={() => handleEdit(row.transaction || row)}
                        aria-label="Visualizar detalhes da transação"
                      >
                        &gt;
                      </Button>
                    </div>
                  )
                }
              ]}
              data={displayRows}
              onRowClick={(row) => {
                if (accountStatementMode && row.rowType === "opening") {
                  openOpeningBalanceDialog(row);
                  return;
                }
                if (accountStatementMode && row.rowType && row.rowType !== "transaction") return;
                handleEdit(row.transaction || row);
              }}
              emptyState={emptyState}
            />
          )}
        </CardContent>
      </Card>

      <Button
        onClick={openCreateDialog}
        className="btn-glow fixed bottom-20 left-1/2 z-40 h-12 w-[220px] -translate-x-1/2 rounded-full px-5 shadow-lg sm:bottom-8 sm:left-auto sm:right-8 sm:w-auto sm:translate-x-0 sm:px-6"
        data-testid="add-transaction-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Nova Transação
      </Button>
      
      {/* Add/Edit Dialog */}
      <AddTransactionDialog 
        open={showAddDialog} 
        onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) {
            setEditData(null);
            setTransactionInitialData(null);
          }
        }}
        editData={editData}
        initialData={editData ? null : transactionInitialData || createTransactionInitialData}
        onDelete={handleDeleteFromDialog}
        onClone={handleCloneTransaction}
        deleteLabel="Excluir transação"
      />
      <Dialog
        open={openingBalanceDialog.open}
        onOpenChange={(open) => {
          if (!open) closeOpeningBalanceDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar saldo inicial</DialogTitle>
            <DialogDescription>
              Informe o saldo correto no início de {monthName}; o sistema cria um ajuste técnico antes do mês.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOpeningBalanceSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Saldo inicial de {monthName}</Label>
              <CurrencyInput
                value={openingBalanceDialog.value}
                currency={statementCurrency}
                onChange={(value) => setOpeningBalanceDialog((current) => ({ ...current, value }))}
                autoFocus
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={closeOpeningBalanceDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 btn-glow">
                Ajustar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={!!balanceAdjustmentToDelete}
        onOpenChange={(open) => {
          if (!open) closeDeleteBalanceAdjustmentDialog();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir ajuste de saldo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O saldo da conta será recalculado sem este ajuste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingBalanceAdjustment}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleDeleteBalanceAdjustment();
              }}
              disabled={isDeletingBalanceAdjustment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingBalanceAdjustment ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MobileTransactionsList({
  transactions,
  isLoading,
  emptyState,
  onEdit,
  onEditOpeningBalance,
  onDeleteBalanceAdjustment,
  onToggleStatus,
  getLinkedTransfer,
  accounts = [],
  statementMode = false,
  statementCurrency = "BRL",
  categories = [],
}) {
  if (isLoading) {
    return (
      <Card className="sm:hidden">
        <CardContent className="p-3 space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!transactions.length) {
    return (
      <Card className="sm:hidden">
        <CardContent className="p-0">{emptyState}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2 sm:hidden">
      {transactions.map((row) => {
        if (statementMode && (row.rowType === "opening" || row.rowType === "daily_balance")) {
          const isOpening = row.rowType === "opening";
          return (
            <div
              key={row.id}
              role={isOpening ? "button" : undefined}
              tabIndex={isOpening ? 0 : undefined}
              className={`flex items-center justify-between gap-3 rounded-lg border bg-muted/40 p-3 text-sm ${isOpening ? "cursor-pointer transition-colors hover:bg-muted" : ""}`}
              onClick={() => {
                if (isOpening) onEditOpeningBalance?.(row);
              }}
              onKeyDown={(event) => {
                if (!isOpening) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onEditOpeningBalance?.(row);
                }
              }}
            >
              <span className="font-semibold text-muted-foreground">{row.description}</span>
              <span className={`font-mono font-bold ${row.balanceAfter < 0 ? "text-destructive" : "text-foreground"}`}>
                {formatCurrency(row.balanceAfter, statementCurrency)}
              </span>
            </div>
          );
        }

        if (statementMode && row.rowType === "balance_adjustment") {
          const amountColor = row.transactionAmount >= 0 ? "text-success" : "text-destructive";
          const pendingClass = row.affectsStatementBalance === false ? "opacity-60" : "";
          return (
            <div
              key={row.id}
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-muted/40 p-3 text-left shadow-sm"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="shrink-0">{formatDate(row.date)}</span>
                </div>
                <div className="truncate text-sm font-bold text-muted-foreground">
                  Ajuste de saldo
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {formatCurrency(row.previousBalance, statementCurrency)} -> {formatCurrency(row.balanceAfter, statementCurrency)}
                </div>
              </div>
              <span className={`font-mono text-sm font-bold ${amountColor} ${pendingClass}`}>
                {row.transactionAmount >= 0 ? "+" : "-"}{formatAmount(row.transactionAmount)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 min-w-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => onDeleteBalanceAdjustment?.(row)}
                aria-label="Excluir ajuste de saldo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        }

        const tx = row.transaction || row;
        const cleanDesc = tx.description ? tx.description.replace(/\s*\(\d+\/\d+\)$/, "") : "Sem descrição";
        const kind = getTransactionKind(tx);
        const linked = isTransferTransaction(tx) ? getLinkedTransfer(tx) : null;
        const accountLabel = getAccountDisplayName(tx, accounts, linked);
        const categoryLabel = isTransferTransaction(tx) && accountLabel !== "—"
          ? accountLabel
          : formatTransactionCategoryName(tx, categories);
        const signedAmount = row.rowType === "transaction"
          ? row.transactionAmount
          : isTransferTransaction(tx)
            ? parseCurrency(tx.amount)
            : getSignedTransactionAmount(tx);
        const neutralTransfer = isTransferTransaction(tx) && !statementMode;
        const amountColor = neutralTransfer
          ? "text-primary"
          : signedAmount >= 0
            ? "text-success"
            : "text-destructive";
        const Icon = tx.status ? CheckCircle2 : Clock3;
        const statusClass = tx.status ? "text-success" : "text-amber-600";

        return (
          <div
            key={row.id || tx.id}
            role="button"
            tabIndex={0}
            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:bg-muted/40"
            onClick={() => onEdit(tx)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onEdit(tx);
              }
            }}
          >
            <div className="min-w-0 space-y-1">
              <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="shrink-0">{formatDate(row.date)}</span>
                <Icon className={`h-3.5 w-3.5 shrink-0 ${statusClass}`} />
                <span className={`truncate font-medium ${statusClass}`}>{tx.status ? "Conciliado" : "Pendente"}</span>
              </div>
              <div className="truncate text-sm font-bold text-foreground">
                {cleanDesc}
              </div>
              <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="truncate">{categoryLabel || "Sem categoria"}</span>
                {kind === TRANSACTION_KINDS.DETAILED && (
                  <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                    Detalhada
                  </span>
                )}
              </div>
              {statementMode && (
                <div className="truncate text-[11px] text-muted-foreground">
                  {formatCurrency(row.previousBalance, statementCurrency)} -> {formatCurrency(row.balanceAfter, statementCurrency)}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className={`font-mono text-sm font-bold ${amountColor} ${row.affectsStatementBalance === false ? "opacity-60" : ""} ${(tx.status && signedAmount < 0 && !neutralTransfer) ? "line-through opacity-70" : ""} ${(tx.status && signedAmount >= 0 && !neutralTransfer) ? "opacity-70" : ""}`}>
                {neutralTransfer ? "" : signedAmount >= 0 ? "+" : "-"}{formatAmount(signedAmount)}
              </span>
              <div className="flex items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
                <Switch
                  checked={!!tx.status}
                  onCheckedChange={() => onToggleStatus(tx)}
                  aria-label="Marcar como conciliado"
                />
                <span className="text-base font-semibold text-muted-foreground">&gt;</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
